import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  ModelAudience,
  ModelPolicy,
  ModelPolicyEdit,
  NewModelPolicy,
  Operator,
  ProviderModel,
  Reason,
  RoutingProfileProvider,
  RoutingProfileSummary,
} from "../admin-client";
import {
  useAttachRoutingProfileProvider,
  useCreateModelPolicy,
  useDetachRoutingProfileProvider,
  useMarkupPlans,
  useProviderAccounts,
  useProviderModels,
  useReorderRoutingProfileProviders,
  useRoutingProfile,
  useSetRoutingProfileProviderStatus,
  useSetRoutingProfileStatus,
  useUpdateModelPolicy,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { FlagBadge, HealthBadge, StatusBadge, limit, when } from "./common";
// The alias grouping and the block that renders it live in their own module
// because the application page renders the same thing — see model-policies.tsx.
import { AliasBlock, audienceLookup, groupByAlias } from "./model-policies";
import { AssignModels } from "./model-assign";
import { profileScope } from "./routing-profiles";
import { RoutingProfileForm, profileCurrency, unitLabel, unitScale } from "./routing-profile-form";
import { formatUnits } from "./money-units";

/** The permissions the Admin API guards this page with; repeated here only to
 *  decide what to draw. */
const WRITE = "providers:write";
const PRICING_READ = "pricing:read";

/** A ceiling shown as an amount, or the fact that there is none. Currency
 *  units rather than the raw micros the column holds: the ceiling an operator
 *  set is "five dollars a request", and 500000000 is the same fact written in
 *  the one form nobody can check at a glance. */
function spendLimit(
  value: number | null,
  scale: { exponent: number; known: boolean },
  currency: string | undefined,
): string {
  return value === null
    ? "no limit"
    : `${formatUnits(value, scale.exponent)} ${unitLabel(currency, scale.known)}`;
}

/**
 * One routing profile, in the order the router reads it.
 *
 * Both tables are sorted by priority rather than by name, because priority IS
 * the behaviour: the first enabled provider that answers is the one that
 * serves the request, and an alphabetical list of the same rows describes
 * something the system never does.
 */
export function RoutingProfileDetail({ profileId }: { profileId: string }) {
  return (
    <RequireOperator>
      {(session) => <ProfilePanels profileId={profileId} operator={session.operator} />}
    </RequireOperator>
  );
}

function ProfilePanels({ profileId, operator }: { profileId: string; operator: Operator }) {
  const profile = useRoutingProfile(profileId);
  const canReadPricing = can(operator, PRICING_READ);
  // Only for the CURRENCY the spend ceilings are denominated in; the profile
  // itself carries none. See profileCurrency.
  const plans = useMarkupPlans({}, { enabled: canReadPricing });
  const [notice, setNotice] = useState<string | null>(null);

  if (profile.isPending) {
    return <Spinner label="Loading the routing profile" />;
  }
  if (profile.error || !profile.data) {
    return <ErrorAlert error={profile.error} title="Could not load the routing profile" />;
  }

  const {
    routing_profile: summary,
    providers,
    model_policies: policies,
    model_audiences: audiences,
  } = profile.data;
  // A copy, because sorting the array the query cache handed over would
  // reorder the cached object every render.
  const chain = [...providers].sort((a, b) => a.priority - b.priority);

  const canWrite = can(operator, WRITE);
  const currency = profileCurrency(plans.data?.markup_plans, summary.markup_plan_id);

  return (
    <>
      <PageHeader
        title={summary.name}
        description={
          <>
            <code>{summary.slug}</code> for {summary.application_name} ·{" "}
            <Link to="/routing-profiles">back to all routing profiles</Link>
          </>
        }
        actions={<StatusBadge status={summary.status} />}
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <ProfileCard
        profile={summary}
        operator={operator}
        canReadPricing={canReadPricing}
        currency={currency}
        onDone={setNotice}
      />
      <ChainCard
        chain={chain}
        profileId={profileId}
        profileName={summary.name}
        canWrite={canWrite}
        onDone={setNotice}
      />
      <PoliciesCard
        policies={policies}
        audiences={audiences}
        chain={chain}
        profileId={profileId}
        canWrite={canWrite}
        onDone={setNotice}
      />
    </>
  );
}

function ProfileCard({
  profile,
  operator,
  canReadPricing,
  currency,
  onDone,
}: {
  profile: RoutingProfileSummary;
  operator: Operator;
  canReadPricing: boolean;
  currency: string | undefined;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetRoutingProfileStatus();
  const [editing, setEditing] = useState(false);
  const next = profile.status === "active" ? "disabled" : "active";
  const scale = unitScale(currency);
  const canWrite = can(operator, WRITE);

  return (
    <Card title="Profile">
      <ErrorAlert error={setStatus.error} title="That status change failed" />
      {canWrite && editing ? (
        <RoutingProfileForm
          target={{ mode: "edit", profile }}
          canReadPricing={canReadPricing}
          onClose={() => setEditing(false)}
          onDone={(_saved, message) => {
            setEditing(false);
            onDone(message);
          }}
        />
      ) : null}
      <dl className="esm-detail-list">
        <dt>Identifier</dt>
        <dd>
          <code>{profile.id}</code>
        </dd>
        <dt>Application</dt>
        <dd>{profile.application_name}</dd>
        <dt>Scope</dt>
        <dd>
          {profileScope(profile)}
          {profile.organization_id === undefined ? null : (
            <>
              {" "}
              <code>{profile.organization_id}</code>
            </>
          )}
        </dd>
        <dt>Markup plan</dt>
        <dd>
          {profile.markup_plan_name ? (
            <>
              {profile.markup_plan_name}{" "}
              {profile.markup_plan_id ? <code>{profile.markup_plan_id}</code> : null}
            </>
          ) : (
            "the platform default plan"
          )}
        </dd>
        <dt>Token ceilings</dt>
        <dd>
          {limit(profile.max_input_tokens)} in / {limit(profile.max_output_tokens)} out
        </dd>
        <dt>Per request</dt>
        <dd>{spendLimit(profile.per_request_limit_micros, scale, currency)}</dd>
        <dt>Daily</dt>
        <dd>{spendLimit(profile.daily_limit_micros, scale, currency)}</dd>
        <dt>Monthly</dt>
        <dd>{spendLimit(profile.monthly_limit_micros, scale, currency)}</dd>
        <dt>Behaviour</dt>
        <dd>
          <div className="esm-badge-row">
            <FlagBadge on={profile.fallback_enabled} label="fallback" />
            <FlagBadge on={profile.edge_enabled} label="edge" />
          </div>
        </dd>
        <dt>Data retention</dt>
        <dd>
          {/* Zero days is "keep nothing", not "keep forever" — the difference
              is the whole point of the setting, so it is spelled out. */}
          {profile.data_retention_days === 0
            ? "nothing retained"
            : `${profile.data_retention_days.toLocaleString()} days`}
        </dd>
        <dt>Created</dt>
        <dd>{when(profile.created_at)}</dd>
      </dl>
      <div className="esm-actions">
        {/* Opening the form is not a write, so it is a plain button. The
            reason prompt sits on Save, where the request actually is. */}
        {canWrite ? (
          <Button variant="ghost" onClick={() => setEditing((open) => !open)}>
            {editing ? "Close editor" : "Edit profile"}
          </Button>
        ) : null}
        {canWrite ? (
          <ReasonButton
            variant={next === "disabled" ? "danger" : "primary"}
            busy={setStatus.isPending}
            mutation={{
              title: next === "disabled" ? `Disable ${profile.name}` : `Enable ${profile.name}`,
              description:
                next === "disabled" ? (
                  <p>
                    {profile.organization_id === undefined
                      ? `Every request from ${profile.application_name} loses this route.`
                      : `Requests from this organization fall back to ${profile.application_name}'s default profile.`}{" "}
                    Nothing upstream is changed; the platform simply stops routing through it.
                  </p>
                ) : (
                  <p>Requests start being routed through this profile's provider chain again.</p>
                ),
              confirmLabel: next === "disabled" ? "Disable profile" : "Enable profile",
              run: async (reason) => {
                const result = await setStatus.mutateAsync({
                  profileID: profile.id,
                  status: next,
                  reason,
                });
                onDone(`${profile.name} is now ${result.status}.`);
              },
            }}
          >
            {next === "disabled" ? "Disable profile" : "Enable profile"}
          </ReasonButton>
        ) : (
          <span className="esm-muted-text">
            Your roles do not grant <code>{WRITE}</code>.
          </span>
        )}
      </div>
    </Card>
  );
}

function ChainCard({
  chain,
  profileId,
  profileName,
  canWrite,
  onDone,
}: {
  chain: RoutingProfileProvider[];
  profileId: string;
  profileName: string;
  canWrite: boolean;
  onDone: (message: string) => void;
}) {
  const reorder = useReorderRoutingProfileProviders();
  const setEntry = useSetRoutingProfileProviderStatus();
  const detach = useDetachRoutingProfileProvider();
  const [attaching, setAttaching] = useState(false);

  /**
   * Move one entry up or down, and send the WHOLE resulting order.
   *
   * A "move this one up" request would have to be resolved against whatever the
   * server holds now, so two operators reordering the same profile would
   * produce an order neither of them chose. Sending the full sequence makes the
   * request describe an outcome, and the server refuses it if the set has
   * changed underneath — which is a reload, not a surprise.
   */
  const move = (index: number, direction: -1 | 1) => {
    const next = [...chain];
    const target = index + direction;
    const moving = next[index];
    const displaced = next[target];
    // Both reads are checked rather than asserted: this is the only thing
    // standing between a bad index and a chain sent with a hole in it, which
    // the server would reject as a mismatch — correctly, but confusingly.
    if (!moving || !displaced) {
      return null;
    }
    next[index] = displaced;
    next[target] = moving;
    return next.map((row) => row.provider_account_id);
  };

  return (
    <Card
      title="Provider chain"
      actions={
        canWrite ? (
          <Button variant="primary" onClick={() => setAttaching((open) => !open)}>
            {attaching ? "Close" : "Add provider"}
          </Button>
        ) : null
      }
    >
      <ErrorAlert error={setEntry.error} title="That entry could not be changed" />
      <ErrorAlert error={detach.error} title="That provider could not be removed" />
      {canWrite && attaching ? (
        <AttachProvider
          profileId={profileId}
          profileName={profileName}
          chain={chain}
          onClose={() => setAttaching(false)}
          onDone={onDone}
        />
      ) : null}
      {/* Account status and health travel with each entry because an enabled
          chain entry pointing at a suspended account is a route that silently
          is not one. */}
      <DataTable<RoutingProfileProvider>
        rows={chain}
        rowKey={(row) => row.id}
        empty="This profile has no provider, so every request through it fails."
        columns={[
          { key: "priority", header: "#", align: "end", render: (row) => row.priority },
          {
            key: "account",
            header: "Account",
            render: (row) => (
              <Link to="/providers/$accountId" params={{ accountId: row.provider_account_id }}>
                {row.label || row.provider}
              </Link>
            ),
          },
          { key: "provider", header: "Provider", render: (row) => <code>{row.provider}</code> },
          {
            key: "environment",
            header: "Environment",
            render: (row) => (
              <Badge tone={row.environment === "prod" ? "warning" : "neutral"}>
                {row.environment}
              </Badge>
            ),
          },
          {
            key: "account_status",
            header: "Account",
            render: (row) => <StatusBadge status={row.account_status} />,
          },
          { key: "health", header: "Health", render: (row) => <HealthBadge health={row.health} /> },
          {
            key: "enabled",
            header: "Entry",
            render: (row) => <FlagBadge on={row.enabled} label="entry" />,
          },
          { key: "weight", header: "Weight", align: "end", render: (row) => row.weight },
          ...(canWrite
            ? [
                {
                  key: "order",
                  header: "Order",
                  render: (row: RoutingProfileProvider) => {
                    const index = chain.findIndex((c) => c.id === row.id);
                    return (
                      <div className="esm-actions">
                        <MoveButton
                          label="Move up"
                          symbol="↑"
                          order={move(index, -1)}
                          row={row}
                          chain={chain}
                          profileId={profileId}
                          profileName={profileName}
                          reorder={reorder}
                          onDone={onDone}
                        />
                        <MoveButton
                          label="Move down"
                          symbol="↓"
                          order={move(index, 1)}
                          row={row}
                          chain={chain}
                          profileId={profileId}
                          profileName={profileName}
                          reorder={reorder}
                          onDone={onDone}
                        />
                      </div>
                    );
                  },
                },
                {
                  key: "actions",
                  header: "",
                  render: (row: RoutingProfileProvider) => (
                    <div className="esm-actions">
                      <EntryToggle
                        row={row}
                        profileId={profileId}
                        profileName={profileName}
                        setEntry={setEntry}
                        onDone={onDone}
                      />
                      <DetachButton
                        row={row}
                        profileId={profileId}
                        profileName={profileName}
                        chainSize={chain.length}
                        detach={detach}
                        onDone={onDone}
                      />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      {canWrite ? (
        <p className="esm-muted-text">
          The first enabled provider that answers serves the request. Reordering this chain also
          reorders every model's fallback order in this profile — a request is resolved by the
          model policy first, so a chain change that did not follow through would not change
          anything. A model policy can only name a provider this chain carries an account for:
          this is the row that authorizes the spend, so adding one here is what makes that
          vendor's models assignable below.
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Put one of the tenant's accounts into this chain.
 *
 * What it does NOT ask is the point, exactly as on AssignModels. The RANK
 * appends, because a new upstream is a fallback until somebody says otherwise
 * — landing it at the top would move live traffic onto a credential nothing
 * has validated yet, and promoting it is what the arrows are for. The WEIGHT
 * takes the column default, because it only breaks ties between equally ranked
 * candidates and an operator adding their first Gemini key has no basis to
 * choose a number.
 *
 * So one decision is left: which account. An account already in the chain is
 * not offered — a second attach is a 409, one upstream having one rank — which
 * leaves this able to say "every account is already here" rather than handing
 * over a picker whose every option is a refusal.
 */
function AttachProvider({
  profileId,
  profileName,
  chain,
  onClose,
  onDone,
}: {
  profileId: string;
  profileName: string;
  chain: RoutingProfileProvider[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const accounts = useProviderAccounts();
  const attach = useAttachRoutingProfileProvider();
  const [accountID, setAccountID] = useState("");

  const held = new Set(chain.map((row) => row.provider_account_id));
  const available = (accounts.data?.provider_accounts ?? [])
    .filter((account) => !held.has(account.id))
    .sort((a, b) =>
      a.provider === b.provider
        ? (a.label || "").localeCompare(b.label || "")
        : a.provider.localeCompare(b.provider),
    );
  const chosen = available.find((account) => account.id === accountID);

  return (
    <div className="esm-subform">
      <h3>Add a provider to this chain</h3>
      <p className="esm-muted-text">
        The account joins at the end of the chain, so it serves only what the providers above it
        could not. Nothing else is asked: reorder with the arrows once you have watched it answer.
      </p>
      <ErrorAlert error={accounts.error} title="The provider accounts could not be read" />
      <ErrorAlert error={attach.error} title="That provider could not be added" />

      {accounts.isPending ? (
        <Spinner label="Loading this tenant's provider accounts" />
      ) : available.length === 0 ? (
        <p className="esm-muted-text">
          {(accounts.data?.provider_accounts ?? []).length === 0
            ? "This tenant holds no provider account yet. Add one on the Providers page first — an account is a credential with a vendor, and this chain is the permission to spend it."
            : "Every provider account this tenant holds is already in this chain."}
        </p>
      ) : (
        <div className="esm-form-grid">
          <Field
            label="Provider account"
            htmlFor="attach-provider-account"
            hint="This tenant's own accounts. One entry per account: a vendor with two keys is two entries, ranked separately."
          >
            <Select
              id="attach-provider-account"
              value={accountID}
              onChange={(event) => setAccountID(event.target.value)}
            >
              <option value="">Choose an account</option>
              {available.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.label || account.provider} · {account.provider} · {account.environment}
                  {account.status === "active" ? "" : ` · ${account.status}`}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {/* A suspended or unvalidated account can be attached on purpose: the
          chain is built before the key is proven, and the entry is simply a
          route nothing takes until the account itself is live again. Saying so
          is better than refusing a decision the server allows. */}
      {chosen && chosen.status !== "active" ? (
        <Alert tone="warning" title={`${chosen.label || chosen.provider} is ${chosen.status}`}>
          It can join the chain, but nothing routes through it until the account itself is active
          again.
        </Alert>
      ) : null}

      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={attach.isPending}
          disabled={!chosen}
          mutation={{
            title: `Add ${chosen ? chosen.label || chosen.provider : "a provider"} to ${profileName}`,
            tone: "primary",
            confirmLabel: "Add provider",
            description: (
              <p>
                {profileName} becomes able to spend this account's credential, at rank{" "}
                {chain.length + 1} of {chain.length + 1} — behind everything already in the chain.
                Models on <code>{chosen ? chosen.provider : "that provider"}</code> can be assigned
                to this profile once it is here.
              </p>
            ),
            run: async (reason) => {
              const name = chosen ? chosen.label || chosen.provider : "That account";
              await attach.mutateAsync({
                profileID: profileId,
                body: { provider_account_id: accountID },
                reason,
              });
              // Cleared rather than closed: a tenant adding Anthropic is
              // usually about to add Gemini, and the invalidated chain drops
              // the one just attached out of the picker by itself.
              setAccountID("");
              onDone(`${name} is number ${chain.length + 1} in the chain.`);
            },
          }}
        >
          Add provider
        </ReasonButton>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The off switch for one entry, which is not the account's own.
 *
 * Suspending the ACCOUNT stops it in every profile that draws on it; this stops
 * it in this one, and keeps the rank — so putting it back is not a second
 * decision about the order.
 */
function EntryToggle({
  row,
  profileId,
  profileName,
  setEntry,
  onDone,
}: {
  row: RoutingProfileProvider;
  profileId: string;
  profileName: string;
  setEntry: ReturnType<typeof useSetRoutingProfileProviderStatus>;
  onDone: (message: string) => void;
}) {
  const name = row.label || row.provider;
  const next = !row.enabled;

  return (
    <ReasonButton
      variant="secondary"
      busy={setEntry.isPending}
      mutation={{
        title: next ? `Enable ${name} here` : `Disable ${name} here`,
        confirmLabel: next ? "Enable entry" : "Disable entry",
        description: next ? (
          <p>
            {profileName} starts trying {name} again, at the rank it already holds. The account
            itself is not changed.
          </p>
        ) : (
          <p>
            {profileName} stops trying {name}, and every model that fell back to it here falls
            through to the next provider in the chain. The rank is kept, and the account keeps
            serving any other profile that carries it.
          </p>
        ),
        run: async (reason) => {
          await setEntry.mutateAsync({
            profileID: profileId,
            accountID: row.provider_account_id,
            enabled: next,
            reason,
          });
          onDone(`${name} is ${next ? "enabled" : "disabled"} in this chain.`);
        },
      }}
    >
      {next ? "Enable" : "Disable"}
    </ReasonButton>
  );
}

/**
 * Withdraw the authorization.
 *
 * Danger, and worded as the money it stops rather than as the row it deletes:
 * this is the only thing that lets a profile spend an account at all, so
 * removing the last one leaves a profile that fails every request. The rank is
 * left as a gap on purpose — renumbering is the reorder route's job, and doing
 * it here would be a second implementation of the cascade into model policies.
 */
function DetachButton({
  row,
  profileId,
  profileName,
  chainSize,
  detach,
  onDone,
}: {
  row: RoutingProfileProvider;
  profileId: string;
  profileName: string;
  chainSize: number;
  detach: ReturnType<typeof useDetachRoutingProfileProvider>;
  onDone: (message: string) => void;
}) {
  const name = row.label || row.provider;

  return (
    <ReasonButton
      variant="danger"
      busy={detach.isPending}
      mutation={{
        title: `Remove ${name} from ${profileName}`,
        tone: "danger",
        confirmLabel: "Remove provider",
        description: (
          <p>
            {profileName} loses the authorization to spend this account.{" "}
            {chainSize === 1
              ? "It is the last provider in this chain, so every request through this profile will fail until another is added."
              : `Models that resolved to ${row.provider} here fall through to the rest of the chain, and any policy naming ${row.provider} stops serving.`}{" "}
            The account itself stays in this tenant's pool, with its credential untouched.
          </p>
        ),
        run: async (reason) => {
          await detach.mutateAsync({
            profileID: profileId,
            accountID: row.provider_account_id,
            reason,
          });
          onDone(`${name} is no longer in ${profileName}'s chain.`);
        },
      }}
    >
      Remove
    </ReasonButton>
  );
}

/**
 * One arrow. Disabled at the ends rather than hidden, so the column does not
 * change width as rows move and an operator does not lose the button they were
 * aiming at.
 */
function MoveButton({
  label,
  symbol,
  order,
  row,
  chain,
  profileId,
  profileName,
  reorder,
  onDone,
}: {
  label: string;
  symbol: string;
  order: string[] | null;
  row: RoutingProfileProvider;
  chain: RoutingProfileProvider[];
  profileId: string;
  profileName: string;
  reorder: ReturnType<typeof useReorderRoutingProfileProviders>;
  onDone: (message: string) => void;
}) {
  if (!order) {
    return (
      <Button variant="secondary" disabled aria-label={label}>
        {symbol}
      </Button>
    );
  }
  const moved = order.indexOf(row.provider_account_id) + 1;
  const name = row.label || row.provider;

  return (
    <ReasonButton
      variant="secondary"
      busy={reorder.isPending}
      mutation={{
        title: `${label}: ${name}`,
        description: (
          <p>
            {name} becomes number {moved} of {chain.length} in {profileName}. Every model's
            fallback order in this profile is renumbered to follow, so this is the order requests
            will actually take.
          </p>
        ),
        confirmLabel: label,
        run: async (reason) => {
          await reorder.mutateAsync({ profileID: profileId, providerAccountIDs: order, reason });
          onDone(`${name} is now number ${moved} in the chain.`);
        },
      }}
    >
      {symbol}
    </ReasonButton>
  );
}


// --------------------------------------------------------------------------
// Model policies
// --------------------------------------------------------------------------

/** What the policy form is for: a new route — carrying the alias it was
 *  opened from, because adding a fallback to an existing alias is the common
 *  case — or one policy that already exists. */
type PolicyFormTarget = { mode: "create"; alias: string } | { mode: "edit"; policy: ModelPolicy };

function PoliciesCard({
  policies,
  audiences,
  chain,
  profileId,
  canWrite,
  onDone,
}: {
  policies: ModelPolicy[];
  audiences: ModelAudience[];
  chain: RoutingProfileProvider[];
  profileId: string;
  canWrite: boolean;
  onDone: (message: string) => void;
}) {
  const [target, setTarget] = useState<PolicyFormTarget | null>(null);
  // The quick path, beside the full one. Ticking a catalogue model derives the
  // alias, appends the rank and leaves every ceiling unset — which is the right
  // answer for most routes and no answer at all for the ones that need a
  // second account of one provider, a hand-picked order or a narrowed ceiling.
  // Those are what "Add policy" is for. Two entry points on ONE page is not the
  // clash two screens would have been: nothing here disagrees about who owns
  // the rows.
  const [assigning, setAssigning] = useState(false);
  const catalogue = useProviderModels(canWrite);
  const groups = groupByAlias(policies);
  const audienceFor = audienceLookup(audiences);
  const aliases = groups.map((group) => group.alias);

  const open = (next: PolicyFormTarget) => {
    setTarget(next);
  };

  return (
    <Card
      title="Model policies"
      actions={
        canWrite ? (
          <div className="esm-actions">
            <Button
              variant="primary"
              onClick={() => {
                setTarget(null);
                setAssigning((open) => !open);
              }}
            >
              {assigning ? "Done assigning" : "Assign models"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAssigning(false);
                open({ mode: "create", alias: "" });
              }}
            >
              Add policy
            </Button>
          </div>
        ) : null
      }
    >
      {/* Keyed by what the form is for, so opening Edit on a second policy
          rebuilds the state rather than leaving the first one's values in the
          boxes under a new heading. */}
      {canWrite && assigning ? (
        <AssignModels
          profileId={profileId}
          assignedModelIds={assignedCatalogueIds(policies, catalogue.data?.models ?? [])}
          onClose={() => setAssigning(false)}
          onDone={onDone}
        />
      ) : null}
      {canWrite && target ? (
        <ModelPolicyForm
          key={target.mode === "create" ? `create:${target.alias}` : target.policy.id}
          profileId={profileId}
          target={target}
          aliases={aliases}
          onClose={() => setTarget(null)}
          onDone={(message) => {
            setTarget(null);
            onDone(message);
          }}
        />
      ) : null}
      {groups.length === 0 ? (
        <p className="esm-muted-text">
          This profile has no model policy, so nothing resolves through it: a request naming any
          model finds no route and fails.
        </p>
      ) : (
        groups.map((group) => (
          <AliasBlock
            key={group.alias}
            group={group}
            chain={chain}
            profileId={profileId}
            audience={audienceFor(group.alias)}
            canWrite={canWrite}
            onAdd={() => open({ mode: "create", alias: group.alias })}
            onEdit={(policy) => open({ mode: "edit", policy })}
            onDone={onDone}
          />
        ))
      )}
    </Card>
  );
}

/**
 * One alias, and the fallback chain behind it.
 *
 * A block per alias rather than one table for the whole profile, because the
 * question an operator brings to this screen is "what happens when somebody
 * asks for X" — and the answer is these rows, in this order, and nothing else
 * on the page.
 */
/**
 * The (provider, provider_model) pair a refusal says has no price, if that is
 * what the refusal was.
 *
 * `WithMeta` extension members are the only reliable way to tell this 409 from
 * the other three a policy write can produce, and they are exactly what lets
 * the sentence be shown against the FIELD that names the pair instead of as a
 * page-level banner: the operator's next move is to change what this box says,
 * or to go and publish a price for what it says now. The cast is because
 * Problem carries the RFC 9457 members it declares and extensions are, by
 * definition, not among them.
 */
function unpricedPair(error: unknown): { provider: string; model: string } | undefined {
  if (!isApiError(error) || error.code !== "conflict" || !error.problem) {
    return undefined;
  }
  const members = error.problem as unknown as Record<string, unknown>;
  const provider = members["provider"];
  const model = members["model"];
  return typeof provider === "string" && typeof model === "string" ? { provider, model } : undefined;
}

interface PolicyFormState {
  model: string;
  catalogue_id: string;
  priority: string;
  max_input_tokens: string;
  max_output_tokens: string;
  max_thinking_tokens: string;
  allow_tools: boolean;
  allow_vision: boolean;
  allow_prompt_caching: boolean;
  allow_reasoning: boolean;
}

function ceilingText(value: number | null): string {
  return value === null ? "" : String(value);
}

/** Empty is "not this route's own ceiling", which the API spells `null` — a
 *  cleared ceiling falls through to the profile's, while a zero would admit
 *  nothing. */
function ceilingValue(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : Number(trimmed);
}

function ceilingError(value: string, zeroMeansSomething: boolean): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Whole tokens only — digits, no separators.";
  }
  if (zeroMeansSomething) {
    return undefined;
  }
  return Number(trimmed) > 0
    ? undefined
    : "Leave it empty to fall through to the profile's ceiling, rather than 0.";
}

function priorityError(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0) {
    return "A positive rank, or empty to append this route after the alias's current last one.";
  }
  return undefined;
}

/**
 * The one form behind "Add policy", "Add a fallback for X" and every row's
 * "Edit".
 *
 * The route's three identifying columns — the alias, the provider and the
 * provider's model id — are fields of the CREATE only. They are not fields of
 * the PATCH at all (the decoder refuses a body carrying one), because moving
 * any of them replaces this policy with a different one: add the new route and
 * archive the old, and both decisions stay in the audit trail with the rank
 * each was given. So the edit half shows them as text, and there is no box
 * that could ask to move them.
 */
function ModelPolicyForm({
  profileId,
  target,
  aliases,
  onClose,
  onDone,
}: {
  profileId: string;
  target: PolicyFormTarget;
  aliases: string[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const models = useProviderModels();
  const create = useCreateModelPolicy();
  const update = useUpdateModelPolicy();
  const creating = target.mode === "create";
  const [form, setForm] = useState<PolicyFormState>(() =>
    creating
      ? {
          model: target.alias,
          catalogue_id: "",
          priority: "",
          max_input_tokens: "",
          max_output_tokens: "",
          max_thinking_tokens: "",
          // 0006's defaults, shown rather than left implicit: an operator who
          // wants a route that may not carry images should have to say so.
          allow_tools: true,
          allow_vision: true,
          allow_prompt_caching: true,
          allow_reasoning: true,
        }
      : {
          model: target.policy.model,
          catalogue_id: "",
          priority: String(target.policy.priority),
          max_input_tokens: ceilingText(target.policy.max_input_tokens),
          max_output_tokens: ceilingText(target.policy.max_output_tokens),
          max_thinking_tokens: ceilingText(target.policy.max_thinking_tokens),
          allow_tools: target.policy.allow_tools,
          allow_vision: target.policy.allow_vision,
          allow_prompt_caching: target.policy.allow_prompt_caching,
          allow_reasoning: target.policy.allow_reasoning,
        },
  );

  const set = (field: keyof PolicyFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));
  const setFlag = (field: keyof PolicyFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value === "yes" }));

  // Retired rows are not offered: ListRoutes filters `pm.status <> 'retired'`,
  // so a policy on one would be admitted here and then never used. The server
  // refuses it too — this only spares the operator the round trip.
  const catalogue = (models.data?.models ?? [])
    .filter((model) => model.status !== "retired")
    .sort((a, b) =>
      a.provider === b.provider
        ? a.model.localeCompare(b.model)
        : a.provider.localeCompare(b.provider),
    );
  const chosen = catalogue.find((model) => model.id === form.catalogue_id);

  const errors = {
    priority: priorityError(form.priority),
    input: ceilingError(form.max_input_tokens, false),
    output: ceilingError(form.max_output_tokens, false),
    thinking: ceilingError(form.max_thinking_tokens, true),
  };
  const ready =
    Object.values(errors).every((error) => error === undefined) &&
    (!creating || (form.model.trim() !== "" && chosen !== undefined));

  // The one refusal that belongs against a field rather than above the form.
  const unpriced = unpricedPair(create.error);
  const label = creating ? "Add policy" : "Save changes";

  async function run(reason: Reason) {
    if (creating) {
      if (!chosen) {
        return;
      }
      const body: NewModelPolicy = {
        model: form.model.trim(),
        provider: chosen.provider,
        provider_model: chosen.model,
        // Omitted APPENDS after the alias's current last policy, which the
        // server computes inside the write. A page cannot do that without
        // racing anybody else adding a policy at the same moment.
        priority: form.priority.trim() === "" ? undefined : Number(form.priority.trim()),
        max_input_tokens: ceilingValue(form.max_input_tokens),
        max_output_tokens: ceilingValue(form.max_output_tokens),
        max_thinking_tokens: ceilingValue(form.max_thinking_tokens),
        allow_tools: form.allow_tools,
        allow_vision: form.allow_vision,
        allow_prompt_caching: form.allow_prompt_caching,
        allow_reasoning: form.allow_reasoning,
      };
      const result = await create.mutateAsync({ profileID: profileId, body, reason });
      onDone(
        `${result.model_policy.model} resolves through ${result.model_policy.provider} at rank ${result.model_policy.priority}.`,
      );
      return;
    }
    const body: ModelPolicyEdit = {
      priority: Number(form.priority.trim()),
      max_input_tokens: ceilingValue(form.max_input_tokens),
      max_output_tokens: ceilingValue(form.max_output_tokens),
      max_thinking_tokens: ceilingValue(form.max_thinking_tokens),
      allow_tools: form.allow_tools,
      allow_vision: form.allow_vision,
      allow_prompt_caching: form.allow_prompt_caching,
      allow_reasoning: form.allow_reasoning,
    };
    const result = await update.mutateAsync({
      profileID: profileId,
      policyID: target.policy.id,
      body,
      reason,
    });
    onDone(`Saved ${result.model_policy.model} on ${result.model_policy.provider}.`);
  }

  if (models.isPending) {
    return <Spinner label="Loading the catalogue" />;
  }

  return (
    <div className="esm-subform">
      <h3>
        {creating
          ? target.alias === ""
            ? "Add a model policy"
            : `Add a route for ${target.alias}`
          : `Edit ${target.policy.model} on ${target.policy.provider}`}
      </h3>
      <p className="esm-muted-text">
        {creating
          ? "The alias is what a client asks for; the catalogue row is what the router calls on its behalf. The two are routinely different strings, and that indirection is what lets one alias fall back across providers."
          : "The alias, the provider and the provider's model id are the route itself, so they cannot be edited: add the new route and archive this one, and both decisions stay in the audit trail with the rank each was given."}
      </p>
      {/* The unpriced 409 is shown against the catalogue box below, so it is
          suppressed here — a sentence in two places reads as two failures. */}
      <ErrorAlert
        error={unpriced ? undefined : (create.error ?? update.error)}
        title={creating ? "That policy was not added" : "That change was not saved"}
      />
      {/* Not a <form>: the write goes through the reason prompt rather than a
          submit, and an Enter key that posted straight past the prompt would
          be a mutation with no reason on it. */}
      <div className="esm-form-grid">
        <Field
          label="Requested model (alias)"
          htmlFor="policy-model"
          hint={
            creating
              ? "What a client puts in the request body. Reuse an alias already on this page to give it a fallback."
              : "Fixed: the alias is part of the route."
          }
        >
          <Input
            id="policy-model"
            value={form.model}
            readOnly={!creating}
            spellCheck={false}
            list={creating ? "policy-alias-options" : undefined}
            onChange={(event) => set("model")(event.target.value)}
          />
        </Field>
        {creating ? (
          <datalist id="policy-alias-options">
            {aliases.map((alias) => (
              <option key={alias} value={alias} />
            ))}
          </datalist>
        ) : null}
        <Field
          label="Served by"
          htmlFor="policy-catalogue"
          hint={
            creating
              ? "A catalogue row. Retired models are not offered — resolution skips them, so a policy naming one would never be used."
              : "Fixed: the provider and its model id are part of the route."
          }
          error={
            unpriced
              ? errorMessage(create.error)
              : undefined
          }
        >
          {creating ? (
            <Select
              id="policy-catalogue"
              value={form.catalogue_id}
              onChange={(event) => set("catalogue_id")(event.target.value)}
            >
              <option value="">Choose a catalogue model…</option>
              {catalogue.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.provider} · {model.model}
                  {model.status === "available" ? "" : ` (${model.status})`}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              id="policy-catalogue"
              value={`${target.policy.provider} · ${target.policy.provider_model}`}
              readOnly
            />
          )}
        </Field>
        <Field
          label="Priority"
          htmlFor="policy-priority"
          hint={
            creating
              ? "The rank within this alias. Empty appends this route after the alias's current last one."
              : "The rank within this alias. Two routes for one alias cannot share one."
          }
          error={errors.priority}
        >
          <Input
            id="policy-priority"
            inputMode="numeric"
            value={form.priority}
            onChange={(event) => set("priority")(event.target.value)}
          />
        </Field>
        <Field
          label="Max input tokens"
          htmlFor="policy-max-input"
          hint="Empty falls through to the profile's ceiling."
          error={errors.input}
        >
          <Input
            id="policy-max-input"
            inputMode="numeric"
            value={form.max_input_tokens}
            onChange={(event) => set("max_input_tokens")(event.target.value)}
          />
        </Field>
        <Field
          label="Max output tokens"
          htmlFor="policy-max-output"
          hint="Empty falls through to the profile's ceiling."
          error={errors.output}
        >
          <Input
            id="policy-max-output"
            inputMode="numeric"
            value={form.max_output_tokens}
            onChange={(event) => set("max_output_tokens")(event.target.value)}
          />
        </Field>
        <Field
          label="Max thinking tokens"
          htmlFor="policy-max-thinking"
          hint="Zero means this route may not think at all; empty takes whatever the model and the profile allow."
          error={errors.thinking}
        >
          <Input
            id="policy-max-thinking"
            inputMode="numeric"
            value={form.max_thinking_tokens}
            onChange={(event) => set("max_thinking_tokens")(event.target.value)}
          />
        </Field>
      </div>
      <h3>What this route may carry</h3>
      <p className="esm-muted-text">
        These are the route's own restrictions, not the model's. Turning one off says &ldquo;this
        route may not carry that even though the model can&rdquo; — the catalogue's capabilities
        are what say whether the model can at all.
      </p>
      <div className="esm-form-grid">
        {(
          [
            { field: "allow_tools", label: "Tools", id: "policy-allow-tools" },
            { field: "allow_vision", label: "Vision", id: "policy-allow-vision" },
            {
              field: "allow_prompt_caching",
              label: "Prompt caching",
              id: "policy-allow-caching",
            },
            { field: "allow_reasoning", label: "Reasoning", id: "policy-allow-reasoning" },
          ] as const
        ).map((flag) => (
          <Field key={flag.field} label={flag.label} htmlFor={flag.id}>
            <Select
              id={flag.id}
              value={form[flag.field] ? "yes" : "no"}
              onChange={(event) => setFlag(flag.field)(event.target.value)}
            >
              <option value="yes">allowed</option>
              <option value="no">not allowed</option>
            </Select>
          </Field>
        ))}
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating
              ? `Add ${form.model.trim() || "a route"}`
              : `Save changes to ${target.mode === "edit" ? target.policy.model : ""}`,
            tone: "primary",
            description: creating ? (
              <p>
                <code>{form.model.trim() || "this alias"}</code> resolves through{" "}
                <code>{chosen ? chosen.model : "the chosen model"}</code> on{" "}
                <code>{chosen ? chosen.provider : "that provider"}</code>. It takes effect on the
                next request this profile serves, provided the profile is enabled and its chain
                carries an account for that provider.
              </p>
            ) : (
              <p>
                Ceilings left empty are CLEARED, which means this route falls through to the
                profile's. Moving the priority changes which upstream is tried first for{" "}
                <code>{target.mode === "edit" ? target.policy.model : ""}</code>.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        <Button variant="ghost" disabled={create.isPending || update.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}


/**
 * Which catalogue rows this profile already routes to.
 *
 * Matched on (provider, provider_model) and NOT on the alias: an alias is
 * several catalogue rows across providers, so matching on it would mark the
 * OpenRouter row as assigned the moment the Anthropic one was — hiding exactly
 * the tick that would have added the fallback.
 */
function assignedCatalogueIds(
  policies: ModelPolicy[],
  catalogue: ProviderModel[],
): ReadonlySet<string> {
  const served = new Set(
    policies.map((policy) => `${policy.provider}\u0000${policy.provider_model}`),
  );
  const assigned = new Set<string>();
  for (const row of catalogue) {
    if (served.has(`${row.provider}\u0000${row.model}`)) {
      assigned.add(row.id);
    }
  }
  return assigned;
}
